// pages/TemplateManager.jsx
import { useState, useEffect } from "react";
import {
  getAllTemplates,
  addTemplate,
  deleteTemplate,
  uploadTemplateImage,
} from "../../api/template";
import { CloudArrowUpIcon } from "@heroicons/react/24/outline";
import Loading from "../loading";
import GetAdmin from "../../utils/getAdmin";
import NotFoundPage from "./404";
import Header from "../Header";
import Navigation from "../Navigation";
import Notification from "../Notification";

const TemplateManager = () => {
  // ---------- state ----------
  const [loading, setLoading] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationProps, setNotificationProps] = useState({});
  const [refresh, setRefresh] = useState(false);

  const [accessPlans, setAccessPlans] = useState("user");
  const [place, setPlace] = useState("homeWork");
  const [selectedGrades, setSelectedGrades] = useState([]);

  // state فرم افزودن
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("withHeader");
  const [page1Url, setPage1Url] = useState("");
  const [page2Url, setPage2Url] = useState("");
  const [uploadingPage1, setUploadingPage1] = useState(false);
  const [uploadingPage2, setUploadingPage2] = useState(false);
  const [page1Error, setPage1Error] = useState("");
  const [page2Error, setPage2Error] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ---------- دریافت اطلاعات کاربر ----------
  useEffect(() => {
    const fetchAdmin = async () => {
      const admin = await GetAdmin();
      setAdminData(admin);
    };
    fetchAdmin();
  }, []);

  // ---------- دریافت لیست قالب‌ها ----------
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const res = await getAllTemplates();
        setTemplates(res.data.templates || []);
      } catch (err) {
        showError("خطا در دریافت قالب‌ها");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (adminData) {
      fetchTemplates();
    }
  }, [adminData, refresh]);

  // ---------- توابع اعلان ----------
  const showSuccess = (msg) => {
    setNotificationProps({ message: msg, type: "success", duration: 3000 });
    setShowNotification(true);
  };

  const showError = (msg) => {
    setNotificationProps({ message: msg, type: "error", duration: 4000 });
    setShowNotification(true);
  };

  // ---------- حذف قالب ----------
  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("آیا از حذف این قالب مطمئن هستید؟")) return;
    try {
      await deleteTemplate(id);
      showSuccess("قالب با موفقیت حذف شد");
      setRefresh((prev) => !prev);
    } catch (err) {
      showError(err.response?.data?.message || "خطا در حذف قالب");
    }
  };

  // ---------- آپلود تصویر (مشترک برای هر دو بخش) ----------
  const handleFileChange = async (e, pageType) => {
    const file = e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 300 * 1024;

    const setUploading =
      pageType === "page1" ? setUploadingPage1 : setUploadingPage2;
    const setUrl = pageType === "page1" ? setPage1Url : setPage2Url;
    const setError = pageType === "page1" ? setPage1Error : setPage2Error;

    if (file.size > MAX_SIZE) {
      showError(
        `حجم فایل نباید بیشتر از ۳۰۷ کیلوبایت باشد (حجم فعلی: ${(file.size / 1024).toFixed(0)} KB)`,
      );
      e.target.value = "";
      // پاک کردن خطا بعد از ۵ ثانیه
      setTimeout(() => setError(""), 5000);
      return;
    }

    setError("");
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await uploadTemplateImage(formData);
      if (res.status === 200) {
        setUrl(res.data.url);
        showSuccess(
          `تصویر ${pageType === "page1" ? "صفحه اول" : "باقی صفحه"} با موفقیت آپلود شد`,
        );
      } else {
        showError(res.data?.message);
      }
    } catch (err) {
      showError("خطا در آپلود تصویر");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // ---------- ایجاد قالب جدید ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !description || !page1Url || !page2Url) {
      showError("لطفاً تمام فیلدها را پر کنید");
      return;
    }

    setSubmitting(true);
    try {
      await addTemplate({
        title: subject,
        description,
        type,
        accessPlans: accessPlans,
        place,
        page1Url,
        page2Url,
        grades: selectedGrades,
      });
      showSuccess("قالب با موفقیت ایجاد شد");
      setSubject("");
      setDescription("");
      setType("withHeader");
      setAccessPlans("user");
      setPlace("homeWork");
      setPage1Url("");
      setPage2Url("");
      setSelectedGrades([]);
      setRefresh((prev) => !prev);
    } catch (err) {
      showError(err.response?.data?.message || "خطا در ایجاد قالب");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleGrade = (grade) => {
    console.log(grade);
    setSelectedGrades((prev) =>
      prev.includes(grade) ? prev.filter((g) => g !== grade) : [...prev, grade],
    );
  };

  // ---------- بررسی دسترسی ----------
  if (!adminData) return <Loading />;
  if (adminData.role !== "manager" && adminData.role !== "questionMaker") {
    return <NotFoundPage />;
  }

  // ---------- رندر ----------
  return (
    <div className="w-full flex justify-center flex-col items-center">
      {showNotification && (
        <Notification
          {...notificationProps}
          setShowNotification={setShowNotification}
          showNotification={showNotification}
        />
      )}

      <Header adminName={adminData.name} />

      <div className="flex flex-col md:flex-row gap-6 justify-between w-full px-4">
        <div className="w-full md:w-1/4 mb-6 lg:mb-0">
          <Navigation />
        </div>

        <div className="w-full md:w-3/4">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              مدیریت قالب‌ها
            </h2>

            {/* ---------- فرم افزودن قالب ---------- */}
            <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* موضوع */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      موضوع
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                      placeholder="مثلاً: قالب مقاله"
                    />
                  </div>

                  {/* انتخاب نوع سربرگ */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      نوع سربرگ
                    </label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="withHeader">سربرگ آماده</option>
                      <option value="noHeader">بدون سربرگ</option>
                      <option value="withBackground">پس زمینه سربرگ</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* accessPlans*/}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      accessPlans
                    </label>
                    <select
                      value={accessPlans}
                      onChange={(e) => setAccessPlans(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="user">رایگان</option>
                      <option value="basic">پایه</option>
                      <option value="professional">حرفه ای</option>
                      <option value="advanced">پیشرفته</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      پایه‌های مورد نظر
                    </label>
                    <div className="flex flex-wrap gap-3 p-3 bg-white rounded-lg border border-gray-300">
                      {["1", "2", "3", "4", "5", "6"].map((grade) => (
                        <label
                          key={grade}
                          className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            value={grade}
                            checked={selectedGrades.includes(grade)}
                            onChange={() => toggleGrade(grade)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          {`پایه ${grade}`}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* آپلود تصویر صفحه اول */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تصویر صفحه اول
                  </label>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, "page1")}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        disabled={uploadingPage1}
                      />
                      <button
                        type="button"
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          uploadingPage1
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                        }`}
                        disabled={uploadingPage1}
                      >
                        <CloudArrowUpIcon className="w-5 h-5" />
                        {uploadingPage1 ? "در حال آپلود..." : "انتخاب تصویر"}
                      </button>
                    </div>
                    {page1Url && (
                      <div className="flex items-center gap-2">
                        <img
                          src={page1Url}
                          alt="صفحه اول"
                          className="h-12 w-12 object-cover rounded-lg border border-gray-300"
                        />
                        <span className="text-xs text-gray-500">
                          (صفحه اول)
                        </span>
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mb-2">
                    حداکثر حجم مجاز: ۳۰۰ کیلوبایت
                  </p>
                </div>

                {/* آپلود تصویر باقی صفحه */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تصویر باقی صفحه
                  </label>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, "page2")}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        disabled={uploadingPage2}
                      />
                      <button
                        type="button"
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          uploadingPage2
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                        }`}
                        disabled={uploadingPage2}
                      >
                        <CloudArrowUpIcon className="w-5 h-5" />
                        {uploadingPage2 ? "در حال آپلود..." : "انتخاب تصویر"}
                      </button>
                    </div>
                    {page2Url && (
                      <div className="flex items-center gap-2">
                        <img
                          src={page2Url}
                          alt="باقی صفحه"
                          className="h-12 w-12 object-cover rounded-lg border border-gray-300"
                        />
                        <span className="text-xs text-gray-500">
                          (باقی صفحه)
                        </span>
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mb-2">
                    حداکثر حجم مجاز: ۳۰۰ کیلوبایت
                  </p>
                </div>

                {/* توضیحات */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    توضیحات
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    placeholder="توضیحات مربوط به قالب..."
                  />
                </div>

                {/* دکمه ثبت */}
                <div className="mt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submitting ? "در حال ذخیره..." : "افزودن قالب"}
                  </button>
                </div>
              </form>
            </div>

            {/* ---------- لیست قالب‌ها ---------- */}
            {loading ? (
              <Loading />
            ) : templates.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                هیچ قالبی یافت نشد
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {templates.map((template) => (
                  <div
                    key={template._id}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        {template.page1Url && (
                          <div className="flex flex-col items-center">
                            <img
                              src={template.page1Url}
                              alt="صفحه اول"
                              className="h-12 w-12 object-cover rounded-lg border border-gray-300"
                            />
                            <span className="text-[10px] text-gray-500 mt-0.5">
                              صفحه اول
                            </span>
                          </div>
                        )}
                        {template.page2Url && (
                          <div className="flex flex-col items-center">
                            <img
                              src={template.page2Url}
                              alt="باقی صفحه"
                              className="h-12 w-12 object-cover rounded-lg border border-gray-300"
                            />
                            <span className="text-[10px] text-gray-500 mt-0.5">
                              باقی صفحه
                            </span>
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-gray-800">
                            {template.title}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {template.description}
                          </p>
                          <span className="inline-block mt-1 text-xs bg-gray-200 px-2 py-0.5 rounded-full text-gray-700">
                            {template.type === "withHeader" && "سربرگ آماده"}
                            {template.type === "noHeader" && "بدون سربرگ"}
                            {template.type === "withBackground" &&
                              "پس زمینه سربرگ"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteTemplate(template._id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      حذف
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateManager;
